import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { publicApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { VisitorShell } from './VisitorShell.tsx';

type Visitor = {
  id: string;
  name: string;
  company_name: string | null;
  host_name: string | null;
  site: string | null;
  status: 'on_site' | 'signed_out';
  registered_at: string;
  signed_out_at: string | null;
};

export function VisitorProof() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const queryKey = ['visitor', id];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => publicApi.get<{ visitor: Visitor }>(`/api/visitor/${id}`),
    enabled: !!id,
  });

  const [done, setDone] = useState(false);
  const signout = useMutation({
    mutationFn: () => publicApi.post(`/api/visitor/${id}/signout`),
    onSuccess: () => {
      setDone(true);
      void qc.invalidateQueries({ queryKey });
    },
  });

  if (isLoading) return <VisitorShell><CenteredSpinner /></VisitorShell>;
  const v = data?.visitor;
  if (!v) {
    return (
      <VisitorShell>
        <p className="text-sm text-slate-600">Visit not found.</p>
      </VisitorShell>
    );
  }

  const onSite = v.status === 'on_site';

  return (
    <VisitorShell>
      <div className="text-center" id="visitor-label">
        <div
          className={`inline-block rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
            onSite ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {onSite ? 'On site' : 'Signed out'}
        </div>
        <h1 className="mt-3 text-2xl font-bold text-slate-900">{v.name}</h1>
        {v.company_name && <div className="text-sm text-slate-500">{v.company_name}</div>}
        <div className="mt-4 space-y-1 text-sm text-slate-600">
          {v.host_name && <div>Host: {v.host_name}</div>}
          {v.site && <div>Site: {v.site}</div>}
          <div>Registered: {new Date(v.registered_at).toLocaleString()}</div>
          {v.signed_out_at && <div>Signed out: {new Date(v.signed_out_at).toLocaleString()}</div>}
        </div>
      </div>

      {onSite ? (
        <div className="mt-6 space-y-3">
          <p className="text-center text-xs text-slate-500">
            Show this screen as proof of registration. Sign out when you leave.
          </p>
          <div className="flex justify-center gap-2">
            <Btn variant="secondary" onClick={() => window.print()}>
              Print label
            </Btn>
            <Btn onClick={() => signout.mutate()} loading={signout.isPending}>
              Sign out
            </Btn>
          </div>
        </div>
      ) : (
        <p className="mt-6 text-center text-sm text-emerald-700">
          {done ? 'You have signed out. Thank you!' : 'This visit is signed out.'}
        </p>
      )}
    </VisitorShell>
  );
}
